import React from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Tooltip,
  useTheme,
} from '@mui/material';
import { useKPIStore } from '../../stores/kpi.store';

interface KPIProgressCardProps {
  title: string;
  value: number;
  unit: string;
}

const KPIProgressCard: React.FC<KPIProgressCardProps> = ({
  title,
  value,
  unit,
}) => {
  const theme = useTheme();
  const { limits } = useKPIStore();
  
  // Find the KPI limit for this statistic
  const kpiLimit = limits.find(limit => {
    // Convert both to uppercase and remove spaces/underscores for comparison
    const normalizedTitle = title.replace(/[\s_]/g, '').toUpperCase();
    const normalizedLimitName = limit.name.replace(/[\s_]/g, '').toUpperCase();
    return normalizedLimitName === normalizedTitle && limit.isEnabled;
  });

  if (!kpiLimit) return null;

  const getProgressColor = (value: number, limit: number, operator: string) => {
    switch (operator) {
      case '>':
        return value >= limit ? theme.palette.success.main : theme.palette.error.main;
      case '<':
        return value <= limit ? theme.palette.success.main : theme.palette.error.main;
      case '>=':
        return value >= limit ? theme.palette.success.main : theme.palette.error.main;
      case '<=':
        return value <= limit ? theme.palette.success.main : theme.palette.error.main;
      case '=':
        return Math.abs(value - limit) < 0.01 ? theme.palette.success.main : theme.palette.error.main;
      default:
        return theme.palette.primary.main;
    }
  };

  const getProgressValue = (value: number, limit: number, operator: string) => {
    switch (operator) {
      case '>':
      case '>=':
        return Math.min((value / limit) * 100, 100);
      case '<':
      case '<=':
        return Math.min((value / limit) * 100, 100);
      case '=':
        return Math.abs(value - limit) < 0.01 ? 100 : 0;
      default:
        return 0;
    }
  };

  const getProgressLabel = (value: number, limit: number, operator: string) => {
    switch (operator) {
      case '>':
        return `${value.toLocaleString()} > ${limit.toLocaleString()} ${unit}`;
      case '<':
        return `${value.toLocaleString()} < ${limit.toLocaleString()} ${unit}`;
      case '>=':
        return `${value.toLocaleString()} ≥ ${limit.toLocaleString()} ${unit}`;
      case '<=':
        return `${value.toLocaleString()} ≤ ${limit.toLocaleString()} ${unit}`;
      case '=':
        return `${value.toLocaleString()} = ${limit.toLocaleString()} ${unit}`;
      default:
        return '';
    }
  };

  const progress = getProgressValue(value, kpiLimit.value, kpiLimit.operator);
  const color = getProgressColor(value, kpiLimit.value, kpiLimit.operator);

  return (
    <Paper
      sx={{
        p: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        borderRadius: '16px',
        bgcolor: theme.palette.background.paper,
        boxShadow: theme.shadows[2],
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'center' }}>
        {title.replace(/_/g, ' ')}
      </Typography>
      
      <Box sx={{ position: 'relative', display: 'inline-flex', mb: 1 }}>
        <CircularProgress
          variant="determinate"
          value={progress}
          size={80}
          thickness={4}
          sx={{
            color,
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round',
            },
          }}
        />
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="caption" component="div" color="text.secondary">
            {`${Math.round(progress)}%`}
          </Typography>
        </Box>
      </Box>

      <Tooltip title={kpiLimit.alertMessage}>
        <Typography variant="body2" color="text.secondary" align="center">
          {getProgressLabel(value, kpiLimit.value, kpiLimit.operator)}
        </Typography>
      </Tooltip>
    </Paper>
  );
};

export default KPIProgressCard; 